import { useEffect, useRef, useState, useCallback } from 'react';
import { useMeetingSelector } from '../meeting/meetingProviderContextDef';

const NOOP = () => {};

const selectMeeting = s => s.meeting;
const selectIsMeetingJoined = s => s.isMeetingJoined;

// Keep at most `max` messages, dropping the oldest. `null`/`Infinity` keeps all.
const capMessages = (arr, max) => {
  if (max == null || !Number.isFinite(max) || arr.length <= max) return arr;
  return arr.slice(arr.length - max);
};

/**
 *
 * @param {string} topic
 * @param {{
 *  onMessageReceived?: Function,
 *  onOldMessagesReceived?: Function,
 *  maxMessages?: number,
 *  bufferMessages?: boolean,
 * }} options
 * @returns {{
 *  publish: (message: string,{persist: boolean, sendOnly: Array<String>}, payload: object) => Promise<void>
 *  messages: Array<{
 *    id: string,
 *    message: string,
 *    senderId: string,
 *    senderName: string,
 *    timestamp: string,
 *    topic: string,
 *    payload: object,
 *  }>
 * }}
 */
const usePubSub = (
  topic,
  {
    onMessageReceived = NOOP,
    onOldMessagesReceived = NOOP,
    // Hard cap on the retained `messages` array. Prevents unbounded growth on
    // high-frequency topics by dropping the oldest messages once the cap is
    // reached. Pass `null` or `Infinity` to keep every message (legacy
    // behavior); raise/lower the number to trade memory for history depth.
    maxMessages = null,
    // When false, incoming messages are delivered only via `onMessageReceived`
    // and the `messages` array is never populated — for high-throughput topics
    // (e.g. thousands/sec) where retaining a re-rendering array is undesirable.
    bufferMessages = true
  } = {}
) => {
  const meeting = useMeetingSelector(selectMeeting);
  const isMeetingJoined = useMeetingSelector(selectIsMeetingJoined);

  const [messages, setMessages] = useState([]);
  const isSubscribed = useRef(false);

  const onMessageReceivedRef = useRef(onMessageReceived);
  const onOldMessagesReceivedRef = useRef(onOldMessagesReceived);
  const meetingRef = useRef(meeting);
  const maxMessagesRef = useRef(maxMessages);
  const bufferMessagesRef = useRef(bufferMessages);

  onMessageReceivedRef.current = onMessageReceived;
  onOldMessagesReceivedRef.current = onOldMessagesReceived;
  meetingRef.current = meeting;
  maxMessagesRef.current = maxMessages;
  bufferMessagesRef.current = bufferMessages;

  /**
   *
   * @param {string} message
   * @param {{persist: boolean}} options
   * @param {object} payload
   * @param {Array<String>} sendOnly
   */
  const publish = useCallback(
    async (message, options, payload) => {
      if (meetingRef.current) {
        return await meetingRef.current.pubSub.publish(
          topic,
          message,
          options,
          payload
        );
      }
    },
    [topic]
  );

  const _handlePubSub = useCallback(data => {
    // Fire the consumer callback synchronously, preserving per-message
    // delivery semantics regardless of buffering settings.
    onMessageReceivedRef.current(data);

    if (!bufferMessagesRef.current) return;

    setMessages(s => capMessages([...s, data], maxMessagesRef.current));
  }, []);

  useEffect(() => {
    if (!meeting || isSubscribed.current || !isMeetingJoined) return;

    isSubscribed.current = true;

    const subscribeToPubsub = async () => {
      const data = await meeting?.pubSub?.subscribe(topic, _handlePubSub);
      if (data) {
        onOldMessagesReceivedRef.current(data.messages);
        if (bufferMessagesRef.current) {
          setMessages(capMessages(data.messages, maxMessagesRef.current));
        }
      }
    };

    subscribeToPubsub();

    return () => {
      isSubscribed.current = false;
      meetingRef.current?.pubSub?.unsubscribe(topic, _handlePubSub);
    };
  }, [meeting, isMeetingJoined, topic, _handlePubSub]);

  return { publish, messages };
};

export default usePubSub;
